/**
 * Utility functions for parsing and handling Obsidian-style wiki links
 * Supports formats: [[Page Name]] and [[Section Name > Page Name]]
 */

/**
 * Extract all wiki link patterns from content
 * @param {string} content - The text content to parse
 * @returns {Array} Array of link objects with { match, linkText, startIndex, endIndex }
 */
export function parseWikiLinks(content) {
  if (!content || typeof content !== 'string') return [];
  
  const links = [];
  const regex = /\[\[([^\]]+)\]\]/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    links.push({
      match: match[0], // Full match including brackets: [[Page Name]]
      linkText: match[1], // Text inside brackets: Page Name or Section > Page
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return links;
}

function normalizeName(value) {
  return String(value || '')
    .replace(/&gt;/gi, '>')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function normalizeNameLoose(value) {
  return normalizeName(value).replace(/[^a-z0-9]/g, '');
}

function normalizeLinkTarget(linkText) {
  let text = String(linkText || '').replace(/&gt;/gi, '>').trim();
  if (!text) return '';

  // Obsidian-style alias support: [[Target|Alias]]
  const aliasIndex = text.indexOf('|');
  if (aliasIndex >= 0) {
    text = text.slice(0, aliasIndex).trim();
  }

  // Strip heading/block refs: [[Page#Heading]] / [[Page^block]]
  const headingIndex = text.indexOf('#');
  const blockIndex = text.indexOf('^');
  const cutIndex = [headingIndex, blockIndex].filter(i => i >= 0).sort((a, b) => a - b)[0];
  if (typeof cutIndex === 'number') {
    text = text.slice(0, cutIndex).trim();
  }

  return text;
}

function splitQualifiedParts(target) {
  return String(target || '')
    .split(/[>›»→]/)
    .map(part => part.trim())
    .filter(Boolean);
}

function toPageRef(candidate) {
  return {
    sectionId: candidate.sectionId,
    sectionName: candidate.sectionName,
    pageId: candidate.pageId,
    pageName: candidate.pageName
  };
}

function buildPageCandidates(sections) {
  const candidates = [];
  const folders = new Map(
    sections
      .filter(section => section?.isFolder)
      .map(folder => [folder.id, folder])
  );

  for (const section of sections) {
    if (section?.isFolder || !section.pages || !Array.isArray(section.pages)) continue;
    const folder = section.parentId ? folders.get(section.parentId) : null;
    for (const page of section.pages) {
      candidates.push({
        folderId: folder?.id || null,
        folderName: folder?.name || '',
        sectionId: section.id,
        sectionName: section.name || 'Untitled Section',
        pageId: page.id,
        pageName: page.name || 'Untitled Page',
        folderNorm: normalizeName(folder?.name),
        sectionNorm: normalizeName(section.name),
        pageNorm: normalizeName(page.name),
        folderLoose: normalizeNameLoose(folder?.name),
        sectionLoose: normalizeNameLoose(section.name),
        pageLoose: normalizeNameLoose(page.name)
      });
    }
  }
  return candidates;
}

/**
 * Find a page by its link text (supports both simple and section-qualified formats)
 * @param {string} linkText - The link text (e.g., "Page Name" or "Section > Page Name")
 * @param {Array} sections - Array of section objects with pages
 * @returns {Object|null} Page object with sectionId, sectionName, pageId, pageName, or null if not found
 */
export function findPageByName(linkText, sections) {
  if (!linkText || !sections || !Array.isArray(sections)) return null;
  const candidates = buildPageCandidates(sections);

  const resolveTarget = (normalizedTarget) => {
    if (!normalizedTarget) return null;

    const targetNorm = normalizeName(normalizedTarget);
    const targetLoose = normalizeNameLoose(normalizedTarget);

    // Qualified links: [[Section > Page]], including legacy multi-part chains like [[Folder > Section > Page]]
    const parts = splitQualifiedParts(normalizedTarget);
    if (parts.length >= 2) {
      const pageToken = parts[parts.length - 1];
      const pageNorm = normalizeName(pageToken);
      const pageLoose = normalizeNameLoose(pageToken);
      const qualifiers = parts.slice(0, -1);
      const qualifierNorms = qualifiers.map(normalizeName);
      const qualifierLoose = qualifiers.map(normalizeNameLoose);

      const exactQualified = candidates.find(c => (
        c.pageNorm === pageNorm &&
        qualifierNorms.every(value => value === c.sectionNorm || value === c.folderNorm)
      ));
      if (exactQualified) return toPageRef(exactQualified);

      const looseQualified = candidates.find(c => (
        c.pageLoose === pageLoose &&
        qualifierLoose.every(value => value === c.sectionLoose || value === c.folderLoose)
      ));
      if (looseQualified) return toPageRef(looseQualified);

      // If section tokens were legacy/renamed, fall back to page-only from the tail token.
      const tailPageMatches = candidates.filter(c => c.pageNorm === pageNorm);
      if (tailPageMatches.length === 1) return toPageRef(tailPageMatches[0]);
      if (tailPageMatches.length > 1) return toPageRef(tailPageMatches[0]);

      const tailPageLooseMatches = candidates.filter(c => c.pageLoose === pageLoose);
      if (tailPageLooseMatches.length === 1) return toPageRef(tailPageLooseMatches[0]);
      if (tailPageLooseMatches.length > 1) return toPageRef(tailPageLooseMatches[0]);
    }

    // Simple page name search - preserve old behavior (first match wins when duplicates exist)
    const directMatches = candidates.filter(c => c.pageNorm === targetNorm);
    if (directMatches.length > 0) return toPageRef(directMatches[0]);

    // Loose fallback for punctuation/spacing legacy variants.
    const looseMatches = candidates.filter(c => c.pageLoose === targetLoose);
    if (looseMatches.length > 0) return toPageRef(looseMatches[0]);

    return null;
  };

  // Prefer the full text first so a real page title containing `|` remains linkable.
  // If that does not resolve, fall back to Obsidian's [[Target|Alias]] syntax.
  const rawTarget = String(linkText).replace(/&gt;/gi, '>').trim();
  const rawMatch = resolveTarget(rawTarget);
  if (rawMatch) return rawMatch;

  return resolveTarget(normalizeLinkTarget(linkText));
}

/**
 * Detect broken links in content (links that don't point to existing pages)
 * @param {string} content - The text content to check
 * @param {Array} sections - Array of section objects with pages
 * @returns {Array} Array of broken link objects with { linkText, match, startIndex, endIndex }
 */
export function detectBrokenLinks(content, sections) {
  const links = parseWikiLinks(content);
  const broken = [];
  
  for (const link of links) {
    const pageRef = findPageByName(link.linkText, sections);
    if (!pageRef) {
      broken.push(link);
    }
  }
  
  return broken;
}

/**
 * Get all pages from all sections in a flat array with section context
 * @param {Array} sections - Array of section objects
 * @returns {Array} Array of page objects with section context
 */
export function getAllPages(sections) {
  if (!sections || !Array.isArray(sections)) return [];
  
  const allPages = [];
  const folders = new Map(
    sections
      .filter(section => section?.isFolder)
      .map(folder => [folder.id, folder])
  );
  
  for (const section of sections) {
    if (section?.isFolder || !section.pages || !Array.isArray(section.pages)) continue;
    const folder = section.parentId ? folders.get(section.parentId) : null;
    
    for (const page of section.pages) {
      const folderName = folder?.name || '';
      const sectionName = section.name || 'Untitled Section';
      const pageName = page.name || 'Untitled Page';
      const pathParts = [folderName, sectionName, pageName].filter(Boolean);
      allPages.push({
        folderId: folder?.id || null,
        folderName,
        sectionId: section.id,
        sectionName,
        pageId: page.id,
        pageName,
        // For autocomplete display
        displayText: pathParts.join(' > '),
        pathParts,
        // For simple search
        searchText: pathParts.join(' ').toLowerCase()
      });
    }
  }
  
  return allPages;
}

/**
 * Filter pages based on search query (for autocomplete)
 * @param {string} query - Search query
 * @param {Array} allPages - Array of page objects from getAllPages
 * @returns {Array} Filtered array of pages
 */
export function filterPagesForAutocomplete(query, allPages) {
  if (!query || !query.trim()) return allPages;

  const queryParts = String(query)
    .replace(/&gt;/gi, '>')
    .split(/[>\u203a\u00bb\u2192]/)
    .map(normalizeName)
    .filter(Boolean);

  if (queryParts.length === 0) return allPages;

  return allPages.filter(page => {
    const pathParts = (page.pathParts || [page.folderName, page.sectionName, page.pageName])
      .filter(Boolean)
      .map(normalizeName);

    if (queryParts.length === 1) {
      return pathParts.some(part => part.includes(queryParts[0]));
    }

    // Match folder/section/page fragments in hierarchy order. Each fragment may
    // be partial, so `Gems > ge` shows pages in the gems section and
    // `Gems > gems > hr` narrows to HRLC.
    let pathIndex = 0;
    return queryParts.every(queryPart => {
      while (pathIndex < pathParts.length) {
        const matches = pathParts[pathIndex].includes(queryPart);
        pathIndex += 1;
        if (matches) return true;
      }
      return false;
    });
  });
}
